import { memo, useCallback } from 'react';
import { Select, SelectProps } from '../../../designSystem/Select';
import { cn } from '../../../lib';
import { TableListFieldItemValue } from '../TableListFieldItemValue';

interface SelectFieldProps<T extends string>
  extends Omit<SelectProps<T>, 'value' | 'values' | 'onValueChange' | 'onValuesChange'> {
  value?: T | null;
  onSave?: (value: T | null | undefined) => void;
  isEditing?: boolean;
  setIsEditing?: (isEditing: boolean) => void;
  actions?: React.ReactNode;
}

function SelectFieldBase<T extends string>({
  value,
  multiple = false,
  onSave,
  actions,
  className,
  withinPortal = true,
  ...rest
}: SelectFieldProps<T>) {
  // Parse value for multiple mode (value is stringified array)
  const parsedValues = multiple && value ? (JSON.parse(value) as T[]) : [];
  const singleValue = multiple ? null : value;

  const handleValueChange = useCallback(
    (newValue: T | null | undefined) => {
      onSave?.(newValue);
    },
    [onSave],
  );

  const handleValuesChange = useCallback(
    (newValues: T[]) => {
      // Stringify array for multiple mode
      const stringified = JSON.stringify(newValues) as T;
      onSave?.(stringified);
    },
    [onSave],
  );

  const renderSelect = () => {
    if (multiple) {
      return (
        <Select
          {...rest}
          triggerButtonVariant="secondary"
          multiple
          values={parsedValues}
          onValuesChange={handleValuesChange}
          menuClassName="w-full max-w-[400px]"
          withinPortal={withinPortal}
        />
      );
    }

    return (
      <Select
        {...rest}
        triggerButtonVariant="secondary"
        value={singleValue}
        onValueChange={handleValueChange}
        menuClassName="w-full max-w-[400px]"
        withinPortal={withinPortal}
      />
    );
  };

  return (
    <TableListFieldItemValue className={cn('px-2 hover:bg-transparent', className)} actions={actions}>
      {renderSelect()}
    </TableListFieldItemValue>
  );
}

export const SelectField = memo(SelectFieldBase) as <T extends string>(
  props: SelectFieldProps<T>,
) => React.ReactElement;
